import type { MenuMatchMode } from './types';

/** First path segments under /inventory that belong to Kho ops, not the item catalog. */
export const INVENTORY_OPS = new Set([
  'stock-in',
  'stock-out',
  'transfers',
  'stocktakes',
  'report',
]);

/** Named /payroll segments that should not highlight the hub item. Empty: structures redirects to ?tab=structures. */
export const PAYROLL_NAMED_SEGMENTS = new Set<string>();

/** Segments under /attendance that are not the hub (index / ?view= / ?tab=). */
export const ATTENDANCE_NON_LIST_SEGMENTS = new Set(['clock', 'record']);

/** Named /scheduling segments that belong to Nghỉ phép, not the Lịch team hub. */
export const SCHEDULING_TIME_OFF_SEGMENTS = new Set(['time-off']);

const isLaboMasterPath = (pathname: string): boolean =>
  pathname.startsWith('/labo/master') || pathname.startsWith('/labo/partners');

export const pathMatches = (
  pathname: string,
  menuPath: string,
  mode: MenuMatchMode = 'prefix'
): boolean => {
  const base = menuPath.split('?')[0];
  if (!base) return false;

  if (mode === 'exact') {
    return pathname === base;
  }

  if (mode === 'catalog') {
    if (pathname === base) return true;
    if (!pathname.startsWith(`${base}/`)) return false;
    const first = pathname.slice(base.length + 1).split('/')[0];
    return !INVENTORY_OPS.has(first);
  }

  if (mode === 'attendanceList') {
    if (pathname === base) return true;
    if (!pathname.startsWith(`${base}/`)) return false;
    const first = pathname.slice(base.length + 1).split('/')[0];
    // ObjectId detail pages stay under list highlight; named segments do not.
    return !ATTENDANCE_NON_LIST_SEGMENTS.has(first);
  }

  if (mode === 'payrollList') {
    if (pathname === base) return true;
    if (!pathname.startsWith(`${base}/`)) return false;
    const first = pathname.slice(base.length + 1).split('/')[0];
    return !PAYROLL_NAMED_SEGMENTS.has(first);
  }

  if (mode === 'schedulingHub') {
    if (pathname === base) return true;
    if (!pathname.startsWith(`${base}/`)) return false;
    const first = pathname.slice(base.length + 1).split('/')[0];
    return !SCHEDULING_TIME_OFF_SEGMENTS.has(first);
  }

  if (mode === 'laboMaster') {
    return isLaboMasterPath(pathname);
  }

  return pathname === base || pathname.startsWith(`${base}/`);
};

/** Active highlight for a menu path (supports ?view= and match modes). */
export const isMenuPathActive = (
  pathname: string,
  search: string,
  menuPath: string,
  match: MenuMatchMode = 'prefix'
): boolean => {
  const [pathName, pathSearch] = menuPath.split('?');
  if (pathSearch) {
    const searchParams = new URLSearchParams(search);
    const pathParams = new URLSearchParams(pathSearch);

    if (pathParams.has('view') && pathname === pathName) {
      return true;
    }

    for (const [key, value] of pathParams.entries()) {
      if (searchParams.get(key) !== value) {
        return false;
      }
    }
    return pathname === pathName;
  }
  return pathMatches(pathname, menuPath, match);
};
